import { useState, useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useForm, useController, useFormContext } from 'react-hook-form';
import { Alert, AlertTitle, Box, CircularProgress, Grid, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Dialog } from '@components/dialog/Dialog';
import { Form } from '@components/forms';
import { FormSection } from '@components/forms/FormSection';
import { TextField } from '@components/textField/TextField';
import { ToastType } from '@components/toast';
import type { CustomerGetResponse } from '@lio/api-types';
import { FormFieldType } from '@models/forms/formFields.type';
import type { TFormField } from '@models/forms/formFields.type';
import { convertUnicodeToAscii } from '@utils/convertUnicodeToAscii';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { getFormattedPolicyNumber } from '@utils/policy';
import { isProductionEnvironment, isLioEmailDomain } from '@/config';
import { useStore } from '@store/GlobalStore';
import {
  useCheckNamedInsuredFeasibility,
  useCreateNamedInsuredUpdate,
} from '@services/customer/useCreateNamedInsuredUpdate';
import type { NamedInsuredFeasibilityWarning } from '@services/customer/useCreateNamedInsuredUpdate';
import { useGetNamedInsuredUpdateRequest } from '@services/customer/useGetNamedInsuredUpdateRequest';
import type { CustomerNamedInsuredUpdateRequest } from '@services/customer/useGetNamedInsuredUpdateRequest';
import { NamedInsuredTransactionsModal } from './NamedInsuredTransactionsModal';

type ModalView = 'entry' | 'confirm' | 'submitToSupport' | 'result';

const MAX_NAME_LENGTH = 160;

type EntryFormValues = { currentName: string; newName: string; confirmName: string };

type BlockingPolicy = {
  id: string;
  policyNumber: string | null;
  policyNumberPrefix: string;
  policyNumberRecord: { policyNumber: string } | null;
};

interface ConfirmNameFieldProps {
  field: TFormField;
  required: boolean;
  disabled: boolean;
  helperText?: ReactNode;
  error: boolean;
}

const CONFIRM_NAME_MISMATCH_MESSAGE = 'Names must match exactly, including capitalization.';

const ConfirmNameField = ({ field, required, disabled, helperText, error }: ConfirmNameFieldProps) => {
  const { getValues } = useFormContext();
  const { field: controllerField } = useController({
    name: field.name,
    rules: {
      required: true,
      validate: (value: string) => {
        const newName = getValues('newName') as string;
        return value.trim() === newName.trim() || CONFIRM_NAME_MISMATCH_MESSAGE;
      },
    },
  });

  const [shouldShowError, setShouldShowError] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <TextField
      fullWidth
      inputLabel={field.label}
      required={required}
      disabled={disabled}
      value={controllerField.value ?? ''}
      name={controllerField.name}
      onChange={(e) => {
        const converted = e.target.value ? convertUnicodeToAscii(e.target.value) : '';
        e.target.value = converted;
        controllerField.onChange(e);
        if (!shouldShowError) {
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => setShouldShowError(true), 600);
        }
      }}
      onBlur={() => {
        controllerField.onBlur();
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setShouldShowError(true);
      }}
      error={shouldShowError && error}
      helperText={shouldShowError ? (helperText as string) : undefined}
      slotProps={{
        htmlInput: {
          maxLength: MAX_NAME_LENGTH,
          onPaste: (e: { preventDefault(): void }) => e.preventDefault(),
        },
      }}
    />
  );
};

const entryFields: Record<string, TFormField> = {
  currentName: {
    name: 'currentName',
    label: 'Current Named Insured Name:',
    fieldType: FormFieldType.Label,
    gridWidth: { xs: 12 },
    sx: { mb: 2 },
    fieldConfig: { textField: { maxLength: MAX_NAME_LENGTH } },
  },
  newName: {
    name: 'newName',
    label: 'Updated Named Insured Name',
    fieldType: FormFieldType.TextField,
    gridWidth: { xs: 12 },
    required: true,
    fieldConfig: { textField: { maxLength: MAX_NAME_LENGTH } },
  },
  confirmName: {
    name: 'confirmName',
    label: 'Confirm Updated Named Insured Name',
    fieldType: FormFieldType.CustomField,
    gridWidth: { xs: 12 },
    required: true,
    fieldConfig: { customField: { Component: ConfirmNameField } },
  },
};

interface Props {
  open: boolean;
  onClose: () => void;
  customer: CustomerGetResponse;
  isExternalUser?: boolean;
  quoteId?: string;
}

export const UpdateNamedInsuredModal = ({ open, onClose, customer, isExternalUser = false, quoteId }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useStore((state) => state.currentUser);
  const isJiraTicketBlocked = !!currentUser && !isProductionEnvironment() && !isLioEmailDomain(currentUser.email);
  const { checkFeasibility } = useCheckNamedInsuredFeasibility(customer.id);
  const { createNamedInsuredUpdate, isPending: isSubmitting } = useCreateNamedInsuredUpdate();
  const { refetch: refetchUpdateRequest } = useGetNamedInsuredUpdateRequest(customer.id);

  const methods = useForm<EntryFormValues>({ mode: 'onChange', defaultValues: { currentName: customer.name } });

  const [view, setView] = useState<ModalView>('entry');
  const [newName, setNewName] = useState('');
  const [updateResult, setUpdateResult] = useState<CustomerNamedInsuredUpdateRequest | null>(null);

  const [feasibilityError, setFeasibilityError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [blockingPolicies, setBlockingPolicies] = useState<BlockingPolicy[]>([]);
  const [isContinuing, setIsContinuing] = useState(false);
  const [warnings, setWarnings] = useState<NamedInsuredFeasibilityWarning[]>([]);
  const [isInternalMdmBlock, setIsInternalMdmBlock] = useState(false);
  const [forceJiraTicket, setForceJiraTicket] = useState(false);

  const handleClose = useCallback(() => {
    methods.reset();
    setView('entry');
    setNewName('');
    setFeasibilityError(null);
    setSubmitError(null);
    setBlockingPolicies([]);
    setWarnings([]);
    setUpdateResult(null);
    setIsInternalMdmBlock(false);
    setForceJiraTicket(false);
    onClose();
  }, [onClose, methods]);

  const watchedNewName = methods.watch('newName') ?? '';
  const watchedConfirmName = methods.watch('confirmName') ?? '';
  const namesMatch = watchedNewName.trim().length > 0 && watchedNewName.trim() === watchedConfirmName.trim();

  useEffect(() => {
    setFeasibilityError(null);
    if (methods.getValues('confirmName')) {
      void methods.trigger('confirmName');
    }
  }, [watchedNewName, methods]);

  const handleContinue = methods.handleSubmit(async (values) => {
    const trimmedNewName = values.newName.trim();

    if (trimmedNewName !== values.confirmName.trim()) {
      methods.setError('confirmName', { message: 'Names do not match.' });
      return;
    }

    if (trimmedNewName === customer.name) {
      methods.setError('newName', { message: 'New name must be different from the current name.' });
      return;
    }

    setFeasibilityError(null);
    setSubmitError(null);
    setBlockingPolicies([]);
    setIsInternalMdmBlock(false);
    setIsContinuing(true);

    try {
      const feasibility = await checkFeasibility(trimmedNewName);

      if (feasibility.isBlocked && !isExternalUser) {
        setFeasibilityError(feasibility.blockMessage ?? 'Named insured update is blocked.');
        setBlockingPolicies(feasibility.blockingPolicies ?? []);
        if (feasibility.mdmMatchFound) {
          setNewName(trimmedNewName);
          setIsInternalMdmBlock(true);
        }
        return;
      }

      setNewName(trimmedNewName);
      setWarnings(feasibility.warnings);

      const hasMultiBrokerageWarning = feasibility.warnings.some((w) => w.type === 'multipleBrokerages');
      const needsSupport = isExternalUser && (feasibility.isBlocked || hasMultiBrokerageWarning);

      if (isExternalUser && !needsSupport) {
        try {
          const result = await createNamedInsuredUpdate({ customerId: customer.id, newName: trimmedNewName, quoteId });
          enqueueSnackbar(
            result.supportTicketCreated
              ? 'The request has been submitted to our support team so they can assist you.'
              : 'Customer Name updated successfully.',
            { variant: ToastType.Success },
          );
          handleClose();
        } catch (submitErr) {
          const message = await getApiErrorMessage(submitErr, 'Failed to update named insured. Please try again.');
          setSubmitError(message);
        }
        return;
      }

      setView(needsSupport ? 'submitToSupport' : 'confirm');
    } catch (error) {
      const message = await getApiErrorMessage(error, 'An error occurred. Please try again.');
      setFeasibilityError(message);
    } finally {
      setIsContinuing(false);
    }
  });

  const handleSubmit = useCallback(
    async (forceTicket?: boolean) => {
      const useForceJiraTicket = forceTicket ?? forceJiraTicket;
      try {
        const result = await createNamedInsuredUpdate({
          customerId: customer.id,
          newName,
          forceJiraTicket: useForceJiraTicket,
          quoteId,
        });

        if (result.supportTicketCreated) {
          enqueueSnackbar('The request has been submitted to our support team so they can assist you.', {
            variant: ToastType.Success,
          });
          handleClose();
          return;
        }

        if (result.endorsementTransactionIds.length === 0 && result.failedPolicyIds.length === 0) {
          enqueueSnackbar('Customer Name updated successfully.', { variant: ToastType.Success });
          handleClose();
          return;
        }

        const { data: fullRequest } = await refetchUpdateRequest();
        setUpdateResult(fullRequest ?? null);
        setView('result');
      } catch (error) {
        const message = await getApiErrorMessage(error, 'Failed to update named insured. Please try again.');
        enqueueSnackbar(message, { variant: ToastType.Error });
      }
    },
    [
      createNamedInsuredUpdate,
      customer.id,
      newName,
      forceJiraTicket,
      quoteId,
      enqueueSnackbar,
      refetchUpdateRequest,
      handleClose,
    ],
  );

  if (view === 'entry') {
    return (
      <Dialog
        open={open}
        title='Update Named Insured'
        handleCloseDialog={handleClose}
        size='medium'
        primaryBtnText={isExternalUser ? 'Submit' : isInternalMdmBlock ? 'Create Support Ticket' : 'Continue'}
        primaryBtnAction={isInternalMdmBlock ? () => void handleSubmit(true) : handleContinue}
        isPrimaryBtnDisabled={
          isInternalMdmBlock
            ? !methods.formState.isValid || !namesMatch || isSubmitting || isJiraTicketBlocked
            : !methods.formState.isValid || !namesMatch || isContinuing || !!feasibilityError
        }
        isPrimaryBtnLoading={isInternalMdmBlock ? isSubmitting : isContinuing}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', mx: 3, mt: 1, mb: 2, gap: 0 }}>
          <Form methods={methods} formId='update-named-insured-form'>
            <Grid container>
              <FormSection fields={entryFields} styleConfig={{ spacing: 0 }} isDisabled={isContinuing} />
            </Grid>
          </Form>

          {feasibilityError && (
            <Alert severity='error'>
              <AlertTitle>Cannot proceed</AlertTitle>
              {feasibilityError}
              {blockingPolicies.length > 0 && (
                <Typography variant='body2' sx={{ mt: 0.5 }}>
                  {blockingPolicies
                    .map(
                      (p) =>
                        p.policyNumber ??
                        getFormattedPolicyNumber(p.policyNumberPrefix, p.policyNumberRecord?.policyNumber ?? p.id),
                    )
                    .join(', ')}
                </Typography>
              )}
              {isInternalMdmBlock && (
                <Typography variant='body2' sx={{ mt: 0.5 }}>
                  Alternatively, you can submit this as a support ticket for our team to process.
                </Typography>
              )}
              {isInternalMdmBlock && isJiraTicketBlocked && (
                <Typography variant='body2' sx={{ mt: 0.5 }}>
                  (Requires a @lioinsurance.com or @liospecialty.com account in this environment.)
                </Typography>
              )}
            </Alert>
          )}

          {submitError && (
            <Alert severity='error'>
              <AlertTitle>Submission failed</AlertTitle>
              {submitError}
            </Alert>
          )}

          {isContinuing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant='body2' color='text.secondary'>
                Checking…
              </Typography>
            </Box>
          )}
        </Box>
      </Dialog>
    );
  }

  if (view === 'submitToSupport') {
    return (
      <Dialog
        open={open}
        title='Request Name Change'
        handleCloseDialog={handleClose}
        size='medium'
        primaryBtnText='Submit to Support'
        primaryBtnAction={() => handleSubmit(true)}
        isPrimaryBtnDisabled={isSubmitting || isJiraTicketBlocked}
        isPrimaryBtnLoading={isSubmitting}
        secondaryBtnText='Exit'
        secondaryBtnAction={handleClose}
        isSecondaryBtnDisabled={isSubmitting}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 3, py: 2 }}>
          {isJiraTicketBlocked && (
            <Alert severity='warning'>
              Submitting Jira tickets requires a @lioinsurance.com or @liospecialty.com account in this environment.
            </Alert>
          )}
          <Typography variant='body1' color='text.main' fontWeight='normal'>
            This change cannot be processed automatically. To request assistance from the LIO Support team, press
            &apos;Submit to Support&apos;.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', alignItems: 'baseline' }}>
            <Typography variant='body2' color='text.secondary' fontWeight='normal'>
              Change from
            </Typography>
            <Typography variant='body1' fontWeight='bold'>
              {customer.name}
            </Typography>
            <Typography variant='body2' color='text.secondary' fontWeight='normal'>
              Change to
            </Typography>
            <Typography variant='body1' fontWeight='bold' color='primary.main'>
              {newName}
            </Typography>
          </Box>

          {warnings.map((w, i) => (
            <Alert key={i} severity='warning'>
              {w.message}
            </Alert>
          ))}
        </Box>
      </Dialog>
    );
  }

  if (view === 'confirm') {
    return (
      <Dialog
        open={open}
        title='Confirm Named Insured Update'
        handleCloseDialog={handleClose}
        size='medium'
        primaryBtnText='Submit'
        primaryBtnAction={() => handleSubmit(false)}
        isPrimaryBtnDisabled={isSubmitting}
        isPrimaryBtnLoading={isSubmitting}
        secondaryBtnText='Back'
        secondaryBtnAction={() => setView('entry')}
        isSecondaryBtnDisabled={isSubmitting}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 3, py: 2 }}>
          <Typography variant='body1' fontWeight='normal' color='text.main'>
            Please confirm the Named Insured name change below. Reminder: this will affect all quotes and policies
            written for this customer -{' '}
            <Box component='span' fontWeight='bold'>
              do so with caution.
            </Box>
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', alignItems: 'baseline' }}>
            <Typography variant='body2' color='text.secondary' fontWeight='normal'>
              From
            </Typography>
            <Typography variant='body1' fontWeight='bold'>
              {customer.name}
            </Typography>
            <Typography variant='body2' color='text.secondary' fontWeight='normal'>
              To
            </Typography>
            <Typography variant='body1' fontWeight='bold' color='primary.main'>
              {newName}
            </Typography>
          </Box>

          {warnings.map((w, i) => (
            <Alert key={i} severity='warning'>
              {w.message}
            </Alert>
          ))}
        </Box>
      </Dialog>
    );
  }

  return (
    <NamedInsuredTransactionsModal
      open={open}
      onClose={handleClose}
      updateRequest={updateResult!}
      title='Named Insured Updated'
      header={
        <Alert severity='info'>
          Customer Name change has occurred, but you must bind these endorsements to complete the change in OneShield.
        </Alert>
      }
    />
  );
};
